import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X } from 'lucide-react';

interface SearchBarProps {
    placeholder?: string;
    className?: string;
    onSearch?: (query: string) => void;
    autoFocus?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
    placeholder = "Потърси продукт, категория или марка...",
    className = "",
    onSearch,
    autoFocus = false
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const activeQuery = new URLSearchParams(location.search).get('query') || '';
    const [query, setQuery] = useState(activeQuery);

    const inputRef = useRef<HTMLInputElement>(null);

    // Keep the bar showing the term that produced the current results, so a reload
    // or a return to /search doesn't hand back an empty box. Leaving the search page
    // keeps the last term instead of wiping it - the X button is the way to clear.
    useEffect(() => {
        if (activeQuery) setQuery(activeQuery);
    }, [activeQuery]);


    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setQuery(value);
    };

    // Handle search submission
    const handleSearch = (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        if (!query.trim()) return;

        // Navigate or callback
        if (onSearch) {
            onSearch(query);
        } else {
            navigate(`/tarsene?query=${encodeURIComponent(query.trim())}`);
        }
    };

    // Clear search
    const handleClear = (e?: React.MouseEvent | React.TouchEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setQuery('');
        inputRef.current?.focus();
    };

    return (
        <div className={`relative select-none ${className}`}>
            <form onSubmit={handleSearch} action="#" method="POST" className="relative">
                <div className="relative flex items-center">
                    <input
                        ref={inputRef}
                        type="search"
                        name="search"
                        value={query}
                        onChange={handleInputChange}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSearch();
                            }
                        }}
                        placeholder={placeholder}
                        autoFocus={autoFocus}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        enterKeyHint="search"
                        className="w-full pl-10 pr-20 py-3 md:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-base md:text-sm"
                    />

                    <Search className="absolute left-3 h-5 w-5 text-gray-400 pointer-events-none" />

                    <div className="absolute right-2 flex items-center gap-1">
                        {query && (
                            <button
                                type="button"
                                onClick={handleClear}
                                onTouchEnd={(e) => {
                                    e.preventDefault();
                                    handleClear(e);
                                }}
                                className="p-2 md:p-1.5 hover:bg-gray-100 active:bg-gray-200 rounded-md transition-colors touch-manipulation"
                            >
                                <X className="h-5 w-5 md:h-4 md:w-4 text-gray-500" />
                            </button>
                        )}

                        <button
                            type="submit"
                            onClick={(e) => {
                                e.preventDefault();
                                handleSearch();
                            }}
                            className="bg-primary-500 text-white px-3 py-2 md:py-1.5 rounded-md hover:bg-primary-600 active:bg-primary-700 transition-colors flex items-center gap-1 touch-manipulation"
                            aria-label="Search"
                        >
                            <Search className="h-5 w-5 md:h-4 md:w-4" />
                            <span className="hidden sm:inline text-sm">Търси</span>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default SearchBar;